import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip, // For the status pills
  Pagination,
  createTheme,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// --- Custom MUI Theme (consistent with previous designs) ---
const palette = {
  primary: {
    main: '#28a745', // Green for accents and buttons
  },
  secondary: {
    main: '#f8f9fa', // Light background for the page
  },
  background: {
    default: '#f0f2f5', // Very light grey background
    paper: '#fff', // White background for cards and panels
  },
  text: {
    primary: '#333',
    secondary: '#6c757d',
  },
  success: { // Define custom success color for chips
    main: '#28a745',
    light: '#e8f5e9', // Lighter green for background
  },
  warning: { // Define custom warning color for chips
    main: '#ffc107',
    light: '#fff3cd', // Lighter yellow for background
  },
  error: { // Define custom error color for chips
    main: '#dc3545',
    light: '#f8d7da', // Lighter red for background
  },
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#28a745', // Green for accents and buttons
    },
    secondary: {
      main: '#f8f9fa', // Light background for the page
    },
    background: {
      default: '#f0f2f5', // Very light grey background
      paper: '#fff', // White background for cards and panels
    },
    text: {
      primary: '#333',
      secondary: '#6c757d',
    },
    success: { // Define custom success color for chips
      main: '#28a745',
      light: '#e8f5e9', // Lighter green for background
    },
    warning: { // Define custom warning color for chips
      main: '#ffc107',
      light: '#fff3cd', // Lighter yellow for background
    },
    error: { // Define custom error color for chips
      main: '#dc3545',
      light: '#f8d7da', // Lighter red for background
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.85rem',
      color: '#6c757d',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#6c757d',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // Rounded corners for sections/cards
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)', // Subtle shadow
          p: 3, // Default padding for Paper components
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px', // Slightly less rounded for general buttons
          padding: '8px 16px',
          fontWeight: 600,
        },
        containedPrimary: {
            backgroundColor: '#28a745',
            color: '#fff',
            '&:hover': {
                backgroundColor: '#218838',
            },
            boxShadow: 'none',
        },
      },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: '8px', // Pill shape for status
                fontWeight: 600,
                fontSize: '0.75rem',
                height: '28px',
                padding: '0 8px',
            },
            colorSuccess: {
                backgroundColor: palette.success.light,
                color: palette.success.main,
            },
            colorWarning: {
                backgroundColor: palette.warning.light,
                color: palette.warning.main,
            },
            colorError: {
                backgroundColor: palette.error.light,
                color: palette.error.main,
            },
        }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#e8f5e9', // Light green background for table header
          '& .MuiTableCell-root': {
            fontWeight: 600,
            color: palette.primary.main, // Green text for headers
          },
        },
      },
    },
    MuiTableRow: {
        styleOverrides: {
            root: {
                '&:last-child td, &:last-child th': { border: 0 }, // Remove border from last row
            }
        }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px', // Adjust cell padding
        },
      },
    },
  },
});

function FundsManagement() {
  const overviewData = [
    {
      icon: <AccountBalanceWalletOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Wallet Balance',
      amount: '₦3,265',
      actionIcon: <MoreHorizIcon />,
      type: 'balance',
    },
    {
      icon: <EventAvailableOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      // title: 'Earnings from Upcoming Bookings',
      amount: '₦3,265',
      actionIcon: <MoreHorizIcon />,
      type: 'earning',
    },
    {
      icon: <CancelOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Earnings from Cancel Patients',
      amount: '₦3,265',
      actionIcon: <MoreHorizIcon />,
      type: 'earning',
    },
    {
      icon: <LocalHospitalOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Expected Earnings from HMO',
      amount: '₦3,265',
      actionIcon: <MoreHorizIcon />,
      type: 'earning',
    },
    {
      icon: <AccountBalanceOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Withdrawal Account',
      account: '8080345678',
      actionIcon: <ArrowForwardIosIcon />,
      type: 'withdrawal',
    },
  ];

  const transactions = [
    {
      id: 1,
      description: 'Consultation fee from Goodness Obialeye',
      transactionNo: '92187102965',
      amount: '₦170,150.00',
      type: 'Credit',
      dateTime: 'Today, 10:30 PM',
      status: 'Successful',
    },
    {
      id: 2,
      description: 'Transfer to GTBank personal account',
      transactionNo: '92187102965',
      amount: '₦50,500.17',
      type: 'Debit',
      dateTime: 'Today, 8:45 PM',
      status: 'In-Progress',
    },
    {
      id: 3,
      description: 'Monthly ConnectedHealth-Pro Subscription Payment',
      transactionNo: '92187102965',
      amount: '₦170,150.00',
      type: 'Credit',
      dateTime: 'Yesterday, 10:30 PM',
      status: 'Successful',
    },
    {
      id: 4,
      description: 'Consultation fee from Matthew Ogbonnaya',
      transactionNo: '92187102965',
      amount: '₦170,150.00',
      type: 'Credit',
      dateTime: 'Yesterday, 10:30 PM',
      status: 'Successful',
    },
    {
      id: 5,
      description: 'Commission percentage from Bayne Pharmacy',
      transactionNo: '92187102965',
      amount: '₦170,150.00',
      type: 'Credit',
      dateTime: 'March 28, 2025, 10:30 PM',
      status: 'Successful',
    },
    {
      id: 6,
      description: 'Commission percentage for patient referral',
      transactionNo: '92187102965',
      amount: '₦170,150.00',
      type: 'Credit',
      dateTime: 'March 25, 2025, 10:30 PM',
      status: 'Successful',
    },
    {
      id: 7,
      description: 'Transfer to GTBank personal account',
      transactionNo: '92187102965',
      amount: '₦50,500.17',
      type: 'Debit',
      dateTime: 'March 26, 2025, 8:45 PM',
      status: 'Cancelled',
    },
  ];

  const [Transactions, setTransactions]= useState([])
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'Successful':
        return 'success';
      case 'In-Progress':
        return 'warning';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', p: 3 }}>
        {/* Header */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Funds Management
        </Typography>

        {/* Overview Cards */}
        <Grid wrap='nowrap' container spacing={2} mb={4}>
          {overviewData.map((card, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}> {/* md={2.4} for 5 items in a row */}
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  {card.icon}
                  <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                    {card.actionIcon}
                  </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {card.title}
                </Typography>
                {card.type === 'balance' || card.type === 'earning' ? (
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {card.amount}
                  </Typography>
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {card.account}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* All Transactions Section */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              All Transactions
            </Typography>
            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              sx={{ color: theme.palette.primary.main, borderColor: theme.palette.primary.main + '80', textTransform: 'none', borderRadius: '8px' }}
            >
              Sort By
            </Button>
          </Box>

          {/* Transaction Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Transaction Description</TableCell>
                  <TableCell>Transaction no</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date - Time</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {row.type === 'Credit' ? (
                          <ArrowDownwardIcon sx={{ color: theme.palette.primary.main, fontSize: '1.2rem' }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ color: theme.palette.error.main, fontSize: '1.2rem' }} />
                        )}
                        <Typography variant="body1">{row.description}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.transactionNo}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.dateTime}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={getStatusChipColor(row.status)}
                        sx={{ px: 1, py: 0.5, borderRadius: '6px' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Pagination count={11} page={1} siblingCount={1} boundaryCount={1} showFirstButton showLastButton />
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}

export default FundsManagement;